import { useCallback, useEffect, useState } from "react";
import { Plus, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, cachedGet, errMsg } from "@/lib/api";
import { Btn, DISCIPLINES, Loading, Pick, StatusPill, fmtDate } from "@/components/app/bits";
import { PhotoStrip, PunchDialog } from "@/components/app/PunchDialog";
import { useSystems } from "./ChecksheetsTab";

function EditPunch({ item, setItem, onSaved, canEdit }) {
  const [f, setF] = useState(null);
  useEffect(() => { if (item) setF({ status: item.status, owner: item.owner || "", due_date: item.due_date || "", category: item.category, photos: item.photos || [] }); }, [item]);
  if (!item || !f) return null;
  const save = async () => {
    try { await api.put(`/punch/${item.id}`, f); toast.success(`${item.number} updated`); setItem(null); onSaved(); } catch (e) { toast.error(errMsg(e)); }
  };
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  return (
    <Dialog open onOpenChange={(o) => !o && setItem(null)}>
      <DialogContent className="bg-[#0F172A] border-[#1E2D4A] max-h-[90vh] overflow-y-auto" data-testid="edit-punch-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{item.number} · <span className="num text-amber-400">{item.tag_no}</span></DialogTitle></DialogHeader>
        <p className="text-slate-300">{item.description}</p>
        <p className="text-xs text-slate-500">Raised by {item.raised_by} on {fmtDate(item.created_at)}{item.closed_at ? ` · cleared ${fmtDate(item.closed_at)}` : ""}</p>
        <div className="grid grid-cols-2 gap-2">
          <Pick testid="edit-punch-status" value={f.status} onChange={set("status")} options={[["open", "Open"], ["in_progress", "In progress"], ["closed", "Closed"]]} />
          <Pick testid="edit-punch-category" value={f.category} onChange={set("category")} options={[["A", "Category A"], ["B", "Category B"]]} />
          <Input value={f.owner} onChange={set("owner")} placeholder="Action by" disabled={!canEdit} data-testid="edit-punch-owner" />
          <Input type="date" value={f.due_date} onChange={set("due_date")} disabled={!canEdit} data-testid="edit-punch-due" />
        </div>
        <PhotoStrip photos={f.photos} onChange={set("photos")} disabled={!canEdit} testid="edit-punch-photo" />
        {canEdit && <Btn onClick={save} data-testid="edit-punch-save">Save changes</Btn>}
      </DialogContent>
    </Dialog>
  );
}

export default function PunchTab({ project }) {
  const { can } = useAuth();
  const systems = useSystems(project.id);
  const [f, setF] = useState({ search: "", category: "", status: "not_closed", discipline: "", system_id: "", owner: "" });
  const [data, setData] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v?.target ? v.target.value : v }));
  const load = useCallback(() => cachedGet(`/projects/${project.id}/punch`, f).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [project.id, f]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const openFull = async (p) => { try { const r = await api.get(`/projects/${project.id}/punch`, { params: { search: p.number } }); setEdit(r.data.items.find((x) => x.id === p.id) || p); } catch { setEdit(p); } };

  return (
    <div className="space-y-4" data-testid="punch-tab">
      <div className="grid grid-cols-2 md:grid-cols-7 gap-2">
        <div className="relative col-span-2">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-500" />
          <Input value={f.search} onChange={set("search")} placeholder="Search number, tag, text" className="pl-9 h-9 bg-[#0F172A]" data-testid="punch-search" />
        </div>
        <Pick testid="punch-filter-category" value={f.category} onChange={set("category")} allLabel="All categories" options={[["A", "Category A"], ["B", "Category B"]]} />
        <Pick testid="punch-filter-status" value={f.status} onChange={set("status")} allLabel="All statuses" options={[["not_closed", "Not closed"], ["open", "Open"], ["in_progress", "In progress"], ["closed", "Closed"]]} />
        <Pick testid="punch-filter-discipline" value={f.discipline} onChange={set("discipline")} allLabel="All disciplines" options={DISCIPLINES} />
        <Pick testid="punch-filter-system" value={f.system_id} onChange={set("system_id")} allLabel="All systems" options={systems.map((s) => [s.id, s.code])} />
        <Pick testid="punch-filter-owner" value={f.owner} onChange={set("owner")} allLabel="All owners" options={data?.owners || []} />
      </div>
      <div className="flex justify-between items-center">
        <span className="font-mono text-xs text-slate-500" data-testid="punch-count">{data ? `${data.total} items` : ""}</span>
        {can("Field") && <Btn onClick={() => setNewOpen(true)} data-testid="new-punch-btn"><Plus className="w-4 h-4" />Raise punch</Btn>}
      </div>
      {!data ? <Loading /> : (
        <div className="panel overflow-x-auto">
          <table className="w-full text-sm min-w-[860px]" data-testid="punch-table">
            <thead><tr className="border-b border-[#1E2D4A] text-left font-mono text-[10px] uppercase tracking-wider text-slate-500">
              {["No.", "Tag", "Cat", "Description", "Discipline", "Action by", "Due", "Status", ""].map((h) => <th key={h} className="px-3 py-3 font-medium">{h}</th>)}
            </tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id} onClick={() => openFull(p)} data-testid={`punch-row-${p.number}`} className="border-b border-[#1E2D4A] last:border-0 hover:bg-[#131C31] cursor-pointer">
                  <td className="px-3 py-3 num text-amber-400">{p.number}</td>
                  <td className="px-3 num text-slate-200">{p.tag_no}</td>
                  <td className="px-3"><span className={`num font-bold ${p.category === "A" ? "text-red-400" : "text-slate-400"}`}>{p.category}</span></td>
                  <td className="px-3 text-slate-300 max-w-xs truncate">{p.description}</td>
                  <td className="px-3 capitalize text-slate-400">{p.discipline}</td>
                  <td className="px-3 text-slate-400">{p.owner}</td>
                  <td className={`px-3 num ${p.status !== "closed" && p.due_date && p.due_date < new Date().toISOString().slice(0, 10) ? "text-red-400" : "text-slate-400"}`}>{fmtDate(p.due_date)}</td>
                  <td className="px-3"><StatusPill status={p.status} /></td>
                  <td className="px-3">{p.photos?.length > 0 && <ImageIcon className="w-4 h-4 text-slate-500" />}</td>
                </tr>
              ))}
              {!data.items.length && <tr><td colSpan={9} className="text-center text-slate-500 py-10">No punch items match these filters.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      <PunchDialog open={newOpen} setOpen={setNewOpen} projectId={project.id} onSaved={load} />
      <EditPunch item={edit} setItem={setEdit} onSaved={load} canEdit={can("Field")} />
    </div>
  );
}
